// src/components/pos/POSBottomNavigation.jsx
import { useState } from 'react'
import { 
  Clock, 
  Package, 
  Truck, 
  Archive, 
  Table, 
  CreditCard, 
  Percent, 
  Users, 
  Trash2,
  X
} from 'lucide-react'
import TableSelectionModal from '../modals/TableSelectionModal'
import PaymentModal from '../modals/PaymentModal'
import HoldOrdersModal from '../modals/HoldOrdersModal'
import CustomerSelectionModal from '../modals/CustomerSelectionModalFixed'
import VIPRoomReservationModal from '../modals/VIPRoomReservationModal'

const POSBottomNavigation = ({
  orderType = 'dine-in',
  onOrderTypeChange,
  onTableSelect,
  selectedTable = null,
  userBranchId = null,
  userRole = null,
  cartItems = [],
  orderCalculation = null,
  customerId = null,
  onCustomerSelect,
  onPaymentSuccess,
  onClearAll,
  onOrderLoad,
  onPayLater,
  currentOrderId = null,
  onOrderIdChange,
  isPaymentModalOpen = false,
  setIsPaymentModalOpen,
  isHoldOrdersModalOpen = false,
  setIsHoldOrdersModalOpen,
  setIsPromotionsModalOpen,
  isVIPRoomsModalOpen = false,
  setIsVIPRoomsModalOpen,
  deliveryProvider = null,
  deliveryReference = '',
  className = "",
  currentCustomer = null,
  webPromotions = [],
  selectedWebPromo = null,
  setSelectedWebPromo
}) => {
  const [showTablesModal, setShowTablesModal] = useState(false)
  const [showCustomersModal, setShowCustomersModal] = useState(false)
  const [showClearAllModal, setShowClearAllModal] = useState(false)
  const [showDeliveryProviderPopup, setShowDeliveryProviderPopup] = useState(false)

  const orderTypes = [
    { id: 'dine-in', label: 'Dine-In', icon: Clock },
    { id: 'takeaway', label: 'Takeaway', icon: Package },
    { 
      id: 'delivery', 
      label: orderType === 'delivery' && deliveryProvider 
        ? (deliveryProvider === 'pickme' ? 'PickMe' : 'Uber')
        : 'Delivery', 
      icon: Truck 
    }
  ]

  const onPayButtonClick = () => {
    if (cartItems && cartItems.length > 0) {
      setIsPaymentModalOpen?.(true)
    }
  }

  const getAllActionButtons = () => [
    { 
      id: 'hold-orders', 
      label: 'Hold Orders', 
      icon: Archive,
      onClick: () => setIsHoldOrdersModalOpen?.(true)
    },
    { 
      id: 'tables', 
      label: 'Tables', 
      icon: Table,
      onClick: () => setShowTablesModal(true)
    },
    { 
      id: 'pay-later', 
      label: 'Pay Later', 
      icon: CreditCard,
      onClick: onPayLater,
      disabled: !cartItems || cartItems.length === 0
    },
    { 
      id: 'vip-rooms', 
      label: 'VIP Rooms', 
      icon: Users,
      onClick: () => setIsVIPRoomsModalOpen?.(true)
    },
    { 
      id: 'discount', 
      label: 'Discount', 
      icon: Percent,
      onClick: () => setIsPromotionsModalOpen?.(true)
    },
    { 
      id: 'customers', 
      label: 'Customers', 
      icon: Users,
      onClick: () => setShowCustomersModal(true)
    },
    { 
      id: 'clear-all', 
      label: 'Clear all', 
      icon: Trash2,
      onClick: () => {
        // Only show modal if there are items to clear
        if (cartItems && cartItems.length > 0) {
          setShowClearAllModal(true)
        } else {
          console.log('🗑️ No items to clear')
        }
      },
      disabled: !cartItems || cartItems.length === 0 // Add disabled state
    }
  ]

  // Filter action buttons based on order type
  const actionButtons = getAllActionButtons().filter(button => {
    // If button has showOnlyFor property, check if current orderType is included
    if (button.showOnlyFor) {
      return button.showOnlyFor.includes(orderType)
    }
    // If no showOnlyFor property, show for all order types
    return true
  })

  // Simple Modal Component
  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">
            {children}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Full Width Bottom Navigation Bar */}
      <div className={`fixed bottom-0 left-0 right-[28rem] bg-white border-t border-gray-200 shadow-lg z-40 flex h-32 ${className}`}>
        
        {/* Left Column - Order Type Buttons (Vertical Stack) */}
        <div className="flex flex-col w-32">
          {orderTypes.map(({ id, label }, index) => (
            <button
              key={id}
              onClick={() => onOrderTypeChange?.(id)}
              className={`flex items-center justify-center h-full transition-colors ${
                orderType === id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } ${index < orderTypes.length - 1 ? 'border-b border-gray-300' : ''}`}
            >
              <span className="text-xs font-medium">{label}</span>
            </button>
          ))}
        </div>

        {/* Middle Section - Action Buttons (2x4 Grid) - Fill remaining space */}
        <div className="flex-1 grid grid-cols-4 grid-rows-2">
          {actionButtons.map(({ id, label, icon, onClick, disabled }) => {
            const IconComponent = icon
            return (
              <button
                key={id}
                onClick={onClick}
                disabled={disabled}
                className={`flex flex-col items-center justify-center transition-colors border-r border-b border-gray-300 last:border-r-0 ${
                  disabled 
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <IconComponent className="h-4 w-4 mb-1" />
                <span className="text-xs font-medium">{label}</span>
              </button>
            )
          })}
        </div>

        {/* Right Section - Pay Button */}
        <div className="w-48 border-l border-gray-300">
          <button
            onClick={() => {
              console.log('🔘 Pay button clicked!')
              if (cartItems && cartItems.length > 0) {
                console.log('✅ Opening payment modal...')
                setIsPaymentModalOpen?.(true)
              } else {
                console.log('❌ Cart is empty or invalid')
              }
            }}
            disabled={!cartItems || cartItems.length === 0}
            className={`w-full h-full flex items-center justify-center transition-colors font-bold text-lg ${
              !cartItems || cartItems.length === 0
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600'
            }`}
          >
            Pay
          </button>
        </div>

      </div>

      {/* Hold Orders Modal */}
      <HoldOrdersModal
        isOpen={isHoldOrdersModalOpen}
        onClose={() => setIsHoldOrdersModalOpen?.(false)}
        branchId={userBranchId}
        currentOrderType={orderType} // Pass current order type for filtering
        currentTable={selectedTable} // Pass current table for filtering
        onOrderSelect={(cartData) => {
          console.log('📋 Order selected from hold orders:', cartData)
          if (onOrderLoad) {
            onOrderLoad(cartData)
          }
        }}
        onOrderDelete={(order) => {
          console.log('🗑️ Order deleted from hold orders:', order.orderNumber)
        }}
      />

      {/* Tables Modal */}
      <TableSelectionModal
        isOpen={showTablesModal}
        onClose={() => setShowTablesModal(false)}
        onTableSelect={(table) => {
          console.log('🪑 Table selected in POS:', table)
          onTableSelect?.(table)
          setShowTablesModal(false)
        }}
        selectedTableId={selectedTable?._id}
        title="Select Table"
        userBranchId={userBranchId} // Pass user's branch ID
      />

      {/* COMMENTED OUT - Pay Later Modal (backup) */}
      {/* <Modal 
        isOpen={showPayLaterModal} 
        onClose={() => setShowPayLaterModal(false)}
        title="Pay Later Orders"
      >
        <div className="space-y-4">
          <p className="text-gray-600">No pay later orders found.</p>
          <div className="flex justify-end">
            <button 
              onClick={() => setShowPayLaterModal(false)}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </Modal> */}

      {/* Customers Modal */}
      <CustomerSelectionModal
        isOpen={showCustomersModal}
        onClose={() => {
          console.log('🔍 [POSBottomNav] Closing customer modal - userRole:', userRole)
          console.log('🔍 [POSBottomNav] Closing customer modal - userBranchId:', userBranchId)
          setShowCustomersModal(false)
        }}
        onCustomerSelect={(customer) => {
          console.log('👤 [POSBottomNav] Customer selected:', customer);
          console.log('🔑 [POSBottomNav] Customer ID:', customer?._id);
          console.log('📞 [POSBottomNav] Customer mobile:', customer?.mobileNumber);
          console.log('🔄 [POSBottomNav] Calling parent onCustomerSelect');
          onCustomerSelect?.(customer);
          console.log('✅ [POSBottomNav] Parent callback completed');
          setShowCustomersModal(false);
          console.log('✅ [POSBottomNav] Modal closed');
        }}
        currentCustomerId={customerId}
        branchId={userBranchId}
        userRole={userRole}
      />

      {/* Clear All Modal */}
      <Modal 
        isOpen={showClearAllModal} 
        onClose={() => setShowClearAllModal(false)}
        title="Clear All Items"
      >
        <div className="space-y-4">
          <div className="text-gray-600">
            <p className="mb-2">Are you sure you want to clear all items from the current order?</p>
            <p className="text-sm text-gray-500">This will remove:</p>
            <ul className="text-sm text-gray-500 list-disc ml-6 mt-1">
              <li>All cart items ({cartItems?.length || 0} items)</li>
              <li>Selected customer</li>
              <li>Selected table</li>
              <li>Order type and delivery provider</li>
            </ul>
            <p className="text-sm text-red-500 mt-2">This action cannot be undone.</p>
          </div>
          <div className="flex space-x-2">
            <button 
              onClick={() => setShowClearAllModal(false)}
              className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={() => {
                console.log('🗑️ Clear all confirmed')
                onClearAll?.()
                setShowClearAllModal(false)
              }}
              className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      </Modal>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen?.(false)}
        cartItems={cartItems}
        orderCalculation={orderCalculation}
        orderType={orderType}
        branchId={userBranchId}
        customerId={customerId}
        selectedTable={selectedTable}
        currentOrderId={currentOrderId} // Pass current order ID
        onOrderIdChange={onOrderIdChange} // Pass order ID change callback
        deliveryProvider={deliveryProvider} // Pass delivery provider
        deliveryReference={deliveryReference} // Pass delivery reference
        currentCustomer={currentCustomer}
        webPromotions={webPromotions}
        selectedWebPromo={selectedWebPromo}
        setSelectedWebPromo={setSelectedWebPromo}
        onPaymentSuccess={(result) => {
          console.log('💰 Payment successful:', result)
          onPaymentSuccess?.(result)
          // NOTE: Do NOT call setIsPaymentModalOpen(false) here.
          // PaymentModal internally shows BillTypeModal and calls onClose() after
          // a 100ms delay. Closing the modal here would unmount PaymentModal
          // before BillTypeModal renders (React state batching race condition).
        }}
      />

      {/* VIP Room Reservation Modal */}
      <VIPRoomReservationModal
        isOpen={isVIPRoomsModalOpen}
        onClose={() => setIsVIPRoomsModalOpen?.(false)}
        branchId={userBranchId}
      />
    </>
  )
}

export default POSBottomNavigation